/* eslint-disable no-nested-ternary */
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import {
  // Components,
  Breakpoints,
  AlignmentShorthand,
  Alignment,
} from '@powdr/constants';
import {
  rgbaTransparencyToHex,
} from '@powdr/utils';

// const CPNT = Components.CONTENT_BLOCK_HERO;

const responsiveShowHide = css`
  &.mobile {
    ${Breakpoints.desktopOnly(css`
      display: none;
    `)}
  }

  &.desktop {
    ${Breakpoints.mobile(css`
      display: none;
    `)}
  }
`;

export const StyledHeroSlide = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`;

export const BackgroundImage = styled(GatsbyImage)`
  grid-area: 1/1;
  height: 100%;
  width: 100%;
  z-index: -1;

  ${Breakpoints.desktopOnly(css`
    ${({ $slideOffsetDirection, $slideOffsetAdjustment, $isAltCopyDisplay }) => (
    ($slideOffsetAdjustment && $slideOffsetDirection && !$isAltCopyDisplay)
      ? `margin-${$slideOffsetDirection}: ${$slideOffsetAdjustment}px; width: auto;`
      : '')};
  `)}

  ${({ $isAltCopyDisplay }) => (($isAltCopyDisplay) ? `
      width: calc(100% - 600px);
      overflow: visible;
      margin-left: 600px;
    ` : '')}
  
  ${responsiveShowHide}
`;

export const BackgroundVideo = styled.div`
  position: relative;
  grid-area: 1/1;
  height: 100%;
  width: 100%;
  z-index: 1;
  overflow: hidden;

  ${Breakpoints.desktopOnly(css`
    ${({ $slideOffsetDirection, $slideOffsetAdjustment }) => (
    ($slideOffsetAdjustment && $slideOffsetDirection)
      ? `margin-${$slideOffsetDirection}: ${$slideOffsetAdjustment}px`
      : '')};
  `)}

  video {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  ${responsiveShowHide}
`;

export const OpacityLayer = styled.div`
  position: relative;
  grid-area: 1/1;
  width: 100%;
  height: 100%;
  z-index: 2;
  background-color: ${({ $color, $opacity }) => `${$color}${rgbaTransparencyToHex($opacity / 100)}`};
`;

export const VideoFrameContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export const VideoFrame = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const rowColumnMap = {
  [AlignmentShorthand.TOP_LEFT]: { col: 'left', row: 'top' },
  [AlignmentShorthand.CENTER_LEFT]: { col: 'left', row: 'center' },
  [AlignmentShorthand.BOTTOM_LEFT]: { col: 'left', row: 'bottom' },
  [AlignmentShorthand.TOP_CENTER]: { col: 'center', row: 'top' },
  [AlignmentShorthand.CENTER_CENTER]: { col: 'center', row: 'center' },
  [AlignmentShorthand.BOTTOM_CENTER]: { col: 'center', row: 'bottom' },
  [AlignmentShorthand.TOP_RIGHT]: { col: 'right', row: 'top' },
  [AlignmentShorthand.CENTER_RIGHT]: { col: 'right', row: 'center' },
  [AlignmentShorthand.BOTTOM_RIGHT]: { col: 'right', row: 'bottom' },
};

const horizontalAlignments = {
  left: `
    justify-content: left;
    text-align: left;
    width: 80%;
  `,
  center: `
    justify-content: center;
    text-align: center;
    width: 100%;
  `,
  right: `
    justify-content: right;
    text-align: right;
    justify-self: end;
    width: 80%;
  `,
};

const verticalAlignments = {
  top: `
    align-items: flex-start;
  `,
  center: `
    align-items: center;
    padding: 0 150px;
  `,
  bottom: `
    align-items: flex-end;
    padding-bottom: 100px;
  `,
};

const wrapperAlignmentStyles = (
  $contentAlignment,
  $slideOffsetAdjustment,
  $slideOffsetDirection,
  $hasOverlayImage,
) => {
  const { col, row } = rowColumnMap[$contentAlignment] || { col: 'center', row: 'center' };

  // Desktop alignment
  const alignmentCss = `
    ${horizontalAlignments[col]};
    ${verticalAlignments[row]};
  `;

  const paddingCss = `
    ${col === 'left' && `padding-left: ${$slideOffsetDirection === Alignment.LEFT
    ? 75 + $slideOffsetAdjustment
    : 75
  }px;`};
    ${col === 'right' && `padding-right: ${$slideOffsetDirection === Alignment.RIGHT
    ? 150 + $slideOffsetAdjustment
    : 150
  }px;`};
    ${row === 'top' && 'padding-top: 50px;'};
  `;

  return css`
    ${Breakpoints.desktopOnly(css`
      ${alignmentCss};
      ${paddingCss};
    `)}

    /* Mobile overrides */
    ${Breakpoints.mobile(css`
      text-align: left;
      justify-content: center;
      width: 100%;
      align-items: center;
      ${$hasOverlayImage ? 'padding: 0 20px;' : 'padding: 0;'};
    `)}
  `;
};

export const SlideContentWrapper = styled.div`
  display: flex;
  grid-area: 1/1;
  position: relative;
  height: auto;
  z-index: 100;
  overflow: hidden;

  ${({
    $isAltCopyDisplay,
    $contentAlignment,
    $slideOffsetAdjustment,
    $slideOffsetDirection,
    $hasOverlayImage,
  }) => ($isAltCopyDisplay
    ? css`
        text-align: left;
        align-items: flex-end;
        width: 600px;
        padding: 0 225px 0px 50px;

        ${Breakpoints.mobile(css`
          width: 100%;
          padding: 0 20px 0px 20px;
        `)}
      `
    : wrapperAlignmentStyles(
      $contentAlignment,
      $slideOffsetAdjustment,
      $slideOffsetDirection,
      $hasOverlayImage,
    ))};

  body.translucent & {
    margin-top: 40px;
  }

  ${responsiveShowHide}
`;
